/**
 * The worksheet builder.
 *
 * Two panes that scroll independently: questions on the left, the printed page on
 * the right. Neither is centred and there is no maximum content width - a 1440px
 * screen should show a full A4 sheet *and* a working editor, which a centred
 * column would make impossible.
 *
 * Collapsed questions are one-line rows, not cards. That single decision is what
 * decides whether a teacher sees twelve questions or six.
 */

import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, saveBlob } from '../../lib/api/client'
import type { WorksheetDetail } from '../../lib/api/client'
import {
  Button,
  Dialog,
  EmptyState,
  Field,
  IconButton,
  Pill,
  Spinner,
  Select,
} from '../../components/ui'
import { Icon } from '../../components/icons'
import { STATUS_TONE } from '../../components/statusTone'
import { toast } from '../../components/toast'
import { Preview } from '../preview/Preview'
import { focusPaperTitle, PaperDetails } from './PaperDetails'
import { SectionList } from './SectionList'
import './builder.css'

const PREVIEW_OPEN_KEY = 'tc.builder.preview-open'
const ZOOM_STEPS = [1, 1.25, 1.5, 2]

export function Builder() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const [saveFailed, setSaveFailed] = useState(false)
  // How many writes are in the air. A count rather than a boolean: a teacher who
  // tabs quickly through three fields starts three saves, and the first to come
  // back must not report "Saved" while the other two are still running.
  const [saving, setSaving] = useState(0)
  const [openId, setOpenId] = useState<string | null>(null)
  const [showAnswers, setShowAnswers] = useState(false)
  // Open on a desktop, closed where the panes stack.
  //
  // Below 1024px builder.css puts the preview *under* a 650px-tall editor, so on
  // a tablet it started roughly 900px down the page and on a phone below the
  // whole question list. Defaulting it closed there means the screen opens on
  // the thing the teacher came to use, with the preview one tap away. The choice
  // is remembered, so anyone who wants it open keeps it open.
  const [previewOpen, setPreviewOpen] = useState(() => {
    const stored = window.localStorage.getItem(PREVIEW_OPEN_KEY)
    if (stored !== null) return stored === '1'
    return window.matchMedia('(min-width: 1025px)').matches
  })
  const [zoom, setZoom] = useState(1)

  useEffect(() => {
    window.localStorage.setItem(PREVIEW_OPEN_KEY, previewOpen ? '1' : '0')
  }, [previewOpen])
  const initialQuestionSelected = useRef(false)

  const paper = useQuery({ queryKey: ['worksheet', id], queryFn: () => api.getWorksheet(id) })
  const answersAllowed = paper.data?.answers_allowed ?? false
  const effectiveShowAnswers = answersAllowed && showAnswers
  const preview = useQuery({
    queryKey: ['preview', id, effectiveShowAnswers],
    queryFn: () => api.preview(id, effectiveShowAnswers),
  })
  const workflow = useQuery({
    queryKey: ['workflow', id],
    queryFn: () => api.workflow(id),
  })

  useEffect(() => {
    if (!answersAllowed) setShowAnswers(false)
  }, [answersAllowed])

  useEffect(() => {
    if (!paper.data || initialQuestionSelected.current) return
    initialQuestionSelected.current = true
    const firstQuestion = paper.data.sections.flatMap((section) => section.questions)[0]
    setOpenId(firstQuestion?.id ?? null)
  }, [paper.data])

  /** Every write goes through here, so the save indicator cannot disagree with
   *  what actually happened. A failed write says so rather than leaving the last
   *  success time on screen over an unsaved change. */
  const started = () => setSaving((count) => count + 1)

  const refresh = () => {
    setSaving((count) => Math.max(0, count - 1))
    setSavedAt(new Date())
    setSaveFailed(false)
    queryClient.invalidateQueries({ queryKey: ['worksheet', id] })
    queryClient.invalidateQueries({ queryKey: ['preview', id] })
  }

  const failed = (error: Error) => {
    setSaving((count) => Math.max(0, count - 1))
    setSaveFailed(true)
    toast(error.message, 'error')
  }

  const undo = useMutation({ mutationFn: () => api.undo(id), onSuccess: refresh, onError: failed })
  const redo = useMutation({ mutationFn: () => api.redo(id), onSuccess: refresh, onError: failed })

  const exportDocx = useMutation({
    mutationFn: (withAnswers: boolean) => api.exportDocx(id, withAnswers),
    onSuccess: (blob, withAnswers) =>
      saveBlob(blob, filenameFor(paper.data, withAnswers)),
    onError: failed,
  })

  if (paper.isLoading) return <Spinner label="Opening the paper" />
  if (!paper.data)
    return (
      <div className="tc-page">
        <EmptyState
          icon={<Icon.papers size={20} />}
          title="That paper no longer exists"
          message="It may have been deleted, or the link may be out of date."
          action={
            <Button variant="primary" onClick={() => navigate('/papers')}>
              Back to my papers
            </Button>
          }
        />
      </div>
    )

  const detail = paper.data
  const editable = detail.editable

  return (
    <div className="tc-builder">
      {/* Two rows, not one.
       *
       * This bar carries up to twelve controls, and as a single wrapping flex row
       * with a `flex: 1` spacer it broke into two or three ragged lines on a
       * 1280px screen - with the spacer throwing whatever landed on each line to
       * opposite ends. Splitting it by job fixes the ordering as well as the
       * wrapping: the top row is what this paper *is* (name, status, whether it
       * saved), the bottom row is what you can *do* to it. */}
      <div className="tc-builder-bar">
        <div className="tc-builder-identity">
          <IconButton label="Back to my papers" variant="ghost" onClick={() => navigate('/papers')}>
            <Icon.back size={16} />
          </IconButton>
          <div className="tc-builder-heading">
            {/* Every paper starts as "Untitled", so renaming is the first thing most
                teachers do - and the name in the toolbar is where they try to do it.
                Leaving it inert sent them hunting through a collapsed panel. */}
            {editable ? (
              <h1 className="tc-builder-title">
                <button
                  type="button"
                  className="tc-builder-rename"
                  title={`Rename “${detail.title}”`}
                  aria-label={`Rename paper: ${detail.title}`}
                  onClick={focusPaperTitle}
                >
                  {detail.title}
                </button>
              </h1>
            ) : (
              <h1 className="tc-builder-title" title={detail.title}>
                {detail.title}
              </h1>
            )}
            <Pill tone={STATUS_TONE[detail.status] ?? 'muted'}>{detail.status_label}</Pill>
            {!editable && (
              <span className="tc-lock-note">
                <Icon.warning size={14} />
                Locked · View or export only
              </span>
            )}
          </div>

          {editable && (
            <div className="tc-builder-save-state">
              <SaveState savedAt={savedAt} saving={saving > 0} failed={saveFailed} editable />
            </div>
          )}
        </div>

        <div className="tc-builder-actions">
        <WorkflowActions
          worksheetId={id}
          workflow={workflow.data}
          onChanged={() => {
            queryClient.invalidateQueries({ queryKey: ['workflow', id] })
            queryClient.invalidateQueries({ queryKey: ['worksheet', id] })
          }}
        />

        <div className="tc-spacer" />

        {editable && (
          <>
            <IconButton
              label="Undo last change"
              size="sm"
              disabled={undo.isPending}
              onClick={() => undo.mutate()}
            >
              <Icon.undo size={16} />
            </IconButton>
            <IconButton
              label="Redo last change"
              size="sm"
              disabled={redo.isPending}
              onClick={() => redo.mutate()}
            >
              <Icon.redo size={16} />
            </IconButton>
          </>
        )}

        {/* Always rendered, as a toggle. It used to appear only while the preview
            was hidden, which meant collapsing the preview shoved every button
            after it sideways. */}
        <Button
          size="sm"
          icon={previewOpen ? <Icon.collapse size={15} /> : <Icon.expand size={15} />}
          aria-pressed={previewOpen}
          title={previewOpen ? 'Hide the printed preview' : 'Show the printed preview'}
          onClick={() => setPreviewOpen((open) => !open)}
        >
          Preview
        </Button>

        <Button
          size="sm"
          icon={<Icon.download size={15} />}
          disabled={exportDocx.isPending}
          title="Pupil version in Word — answers are hidden"
          onClick={() => exportDocx.mutate(false)}
        >
          Pupil copy
        </Button>
        {/* The reason lives on screen, not only in a tooltip: a disabled button
            cannot take focus, so `title` alone is mouse-only. Same treatment as
            "Drafts only" on My papers. */}
        <span className="tc-builder-export">
          <Button
            size="sm"
            icon={<Icon.download size={15} />}
            disabled={exportDocx.isPending || !answersAllowed}
            title={
              answersAllowed
                ? 'Teacher version in Word — includes correct answers and answer key'
                : 'Teacher copy is disabled for exam sheets'
            }
            onClick={() => exportDocx.mutate(true)}
          >
            Teacher copy
          </Button>
          {!answersAllowed && <span className="tc-disabled-action-note">Not for exams</span>}
        </span>
        </div>
      </div>

      <div className={previewOpen ? 'tc-panes' : 'tc-panes is-preview-collapsed'}>
        <section className="tc-pane-edit" aria-label="Questions">
          <SectionList
            detail={detail}
            totalMarks={preview.data?.total_marks}
            editable={editable}
            openId={openId}
            onOpen={setOpenId}
            editorHeader={
              <PaperDetails
                detail={detail}
                editable={editable}
                onSaved={refresh}
                onSaving={started}
                onFailed={failed}
              />
            }
            onSaved={refresh}
            onSaving={started}
            onFailed={failed}
          />
        </section>

        {previewOpen && (
          <section className="tc-pane-preview" aria-label="Preview">
            <div className="tc-preview-head">
              <span className="tc-field-label">Preview</span>
              <div className="tc-preview-controls">
                <label className="tc-check">
                  <input
                    type="checkbox"
                    checked={showAnswers}
                    disabled={!answersAllowed}
                    onChange={(event) => setShowAnswers(event.target.checked)}
                  />
                  <span>Show answers</span>
                </label>
                {!answersAllowed && (
                  <span className="tc-field-hint">Disabled for exam sheets</span>
                )}
                {/* The sheet is drawn to scale, which at this pane width puts
                    11.5pt body text at roughly 8px on screen - correct, and too
                    small to proofread. Zoom magnifies the sheet without changing
                    the scale it is drawn at. */}
                <div className="tc-preview-zoom" role="group" aria-label="Preview zoom">
                  <IconButton
                    label="Zoom out"
                    size="sm"
                    variant="ghost"
                    disabled={zoom === ZOOM_STEPS[0]}
                    onClick={() => setZoom((z) => ZOOM_STEPS[Math.max(0, ZOOM_STEPS.indexOf(z) - 1)])}
                  >
                    <Icon.minus size={15} />
                  </IconButton>
                  <span aria-live="polite">{Math.round(zoom * 100)}%</span>
                  <IconButton
                    label="Zoom in"
                    size="sm"
                    variant="ghost"
                    disabled={zoom === ZOOM_STEPS[ZOOM_STEPS.length - 1]}
                    onClick={() =>
                      setZoom((z) =>
                        ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, ZOOM_STEPS.indexOf(z) + 1)],
                      )
                    }
                  >
                    <Icon.plus size={15} />
                  </IconButton>
                </div>
                <IconButton
                  label="Hide preview"
                  size="sm"
                  variant="ghost"
                  onClick={() => setPreviewOpen(false)}
                >
                  <Icon.collapse size={16} />
                </IconButton>
              </div>
            </div>
            <div className="tc-preview-zoom-frame" style={{ '--tc-preview-zoom': zoom } as CSSProperties}>
              <Preview data={preview.data} loading={preview.isLoading} error={preview.isError} />
            </div>
          </section>
        )}
      </div>
    </div>
  )
}

function WorkflowActions({
  worksheetId,
  workflow,
  onChanged,
}: {
  worksheetId: string
  workflow: import('../../lib/api/client').WorkflowState | undefined
  onChanged: () => void
}) {
  const [action, setAction] = useState<string | null>(null)
  const [reviewerId, setReviewerId] = useState('')
  const selected = workflow?.actions.find((item) => item.action === action)

  const perform = useMutation({
    mutationFn: () =>
      api.perform(worksheetId, {
        action: action!,
        reviewer_id: action === 'submit_for_review' ? reviewerId || null : null,
        comment: null,
      }),
    onSuccess: () => {
      setAction(null)
      onChanged()
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  const begin = (next: string) => {
    setReviewerId(workflow?.default_reviewer_id ?? workflow?.reviewers[0]?.id ?? '')
    setAction(next)
  }

  return (
    <>
      {(workflow?.actions ?? []).map((item) => (
        <Button
          key={item.action}
          size="sm"
          variant={item.action === 'submit_for_review' ? 'primary' : 'secondary'}
          onClick={() => begin(item.action)}
        >
          {item.label}
        </Button>
      ))}
      <Dialog
        open={action !== null}
        title={selected?.label ?? 'Move this paper?'}
        onClose={() => setAction(null)}
        footer={
          <>
            <Button onClick={() => setAction(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={
                perform.isPending || (action === 'submit_for_review' && !reviewerId)
              }
              onClick={() => perform.mutate()}
            >
              Confirm
            </Button>
          </>
        }
      >
        {action === 'submit_for_review' ? (
          <>
            <p>
              The paper is locked while it is checked. The reviewer can approve it or
              send it back with comments.
            </p>
            <Field label="Reviewer">
              <Select
                value={reviewerId}
                onChange={(event) => setReviewerId(event.target.value)}
              >
                <option value="">Choose a reviewer</option>
                {(workflow?.reviewers ?? []).map((reviewer) => (
                  <option key={reviewer.id} value={reviewer.id}>
                    {reviewer.full_name}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        ) : (
          <p>
            The paper is frozen exactly as it is now and sent directly to the printing
            team. You cannot edit it after confirming.
          </p>
        )}
      </Dialog>
    </>
  )
}

/**
 * What actually happened, never a permanent tick.
 *
 * Before the first edit there is nothing to report. After one, the teacher is told
 * when it was stored. A failed write says so, because the change is still on screen
 * looking exactly like a saved one.
 */
function SaveState({
  savedAt,
  saving,
  failed,
  editable,
}: {
  savedAt: Date | null
  saving: boolean
  failed: boolean
  editable: boolean
}) {
  if (!editable) return null
  // In flight beats everything: while a write is outstanding, neither the last
  // success time nor a stale failure describes what is true right now. This is
  // also the state that tells a teacher not to close the tab yet - fields commit
  // on blur, so "Saved at 14:32" next to an unwritten change was the one message
  // that could cost them work.
  if (saving)
    return (
      <span className="tc-saving" role="status">
        <span className="tc-saving-dot" aria-hidden="true" />
        Saving…
      </span>
    )
  if (failed)
    return (
      <span className="tc-save-failed" role="status">
        Not saved — your last change has not been stored.
      </span>
    )
  if (!savedAt)
    return <span className="tc-field-hint">Saved after you leave a field.</span>
  return (
    <span className="tc-saved" role="status">
      <Icon.check size={14} /> Saved at{' '}
      {savedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
    </span>
  )
}

function filenameFor(paper: WorksheetDetail | undefined, withAnswers: boolean) {
  const base = (paper?.title ?? 'worksheet').replace(/[^\w\s-]/g, '').trim().replace(/[\s_]+/g, '_')
  return `${base || 'worksheet'}${withAnswers ? '_teacher_copy' : ''}.docx`
}
